import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Save, Shield } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/contexts/AdminAuthContext';

interface AdminFormData {
  username: string;
  full_name: string;
  email: string;
  is_active: boolean;
  new_password: string;
  confirm_password: string;
}

const AdminForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [formData, setFormData] = useState<AdminFormData>({
    username: '',
    full_name: '',
    email: '',
    is_active: true,
    new_password: '',
    confirm_password: '',
  });

  useEffect(() => {
    if (id) {
      loadAdmin();
    }
  }, [id]);

  const loadAdmin = async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      // Get admin data with email from a query that joins with auth.users view
      const { data: adminData, error: adminError } = await supabase
        .from('admin_users')
        .select('username, full_name, is_active, is_super_admin')
        .eq('id', id)
        .single();

      if (adminError) throw adminError;

      // Try to get email - use username as fallback
      let userEmail = adminData.username;
      
      // Query to get email from auth schema (if accessible)
      try {
        const { data: userData } = await supabase
          .rpc('get_user_email', { user_id: id });
        
        if (userData) {
          userEmail = userData;
        }
      } catch (e) {
        console.log('Could not fetch email, using username');
      }

      setFormData({
        username: adminData.username,
        full_name: adminData.full_name || '',
        email: userEmail,
        is_active: adminData.is_active,
        new_password: '',
        confirm_password: '',
      });

      setIsSuperAdmin(adminData.is_super_admin || false);
      setIsOwnProfile(user?.id === id);
    } catch (error) {
      console.error('Erro ao carregar administrador:', error);
      toast.error('Erro ao carregar dados do administrador');
      navigate('/admin/admins');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSuperAdmin) {
      toast.error('Não é possível editar um Super Administrador!');
      return;
    }

    if (!formData.username.trim() || !formData.full_name.trim()) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    // Validar senha se foi preenchida
    if (formData.new_password || formData.confirm_password) {
      // Só permitir trocar senha se for o próprio perfil
      if (!isOwnProfile) {
        toast.error('Você só pode alterar sua própria senha!');
        return;
      }

      if (formData.new_password !== formData.confirm_password) {
        toast.error('As senhas não coincidem');
        return;
      }

      if (formData.new_password.length < 6) {
        toast.error('A senha deve ter no mínimo 6 caracteres');
        return;
      }

      // Validar requisitos de senha
      const hasUpperCase = /[A-Z]/.test(formData.new_password);
      const hasLowerCase = /[a-z]/.test(formData.new_password);
      const hasNumber = /[0-9]/.test(formData.new_password);

      if (!hasUpperCase || !hasLowerCase || !hasNumber) {
        toast.error('A senha deve conter letras maiúsculas, minúsculas e números');
        return;
      }
    }

    setIsLoading(true);
    try {
      console.log('Atualizando admin:', { id, isOwnProfile, hasPassword: !!formData.new_password });
      
      // Update admin_users
      const { error: updateError } = await supabase
        .from('admin_users')
        .update({
          username: formData.username.trim(),
          full_name: formData.full_name.trim(),
          is_active: formData.is_active,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (updateError) throw updateError;

      // Update password if provided - use Supabase updateUser
      if (formData.new_password && isOwnProfile) {
        console.log('Tentando atualizar senha...');
        const { error: passwordError } = await supabase.auth.updateUser({
          password: formData.new_password
        });

        if (passwordError) {
          console.error('Erro ao atualizar senha:', passwordError);
          toast.error(`Dados atualizados, mas erro ao alterar senha: ${passwordError.message}`);
          navigate('/admin/admins');
          return;
        }

        console.log('Senha atualizada com sucesso');
        toast.success('Administrador e senha atualizados com sucesso!');
      } else {
        if (formData.new_password && !isOwnProfile) {
          console.log('Senha não atualizada: não é o próprio perfil');
        }
        toast.success('Administrador atualizado com sucesso!');
      }

      navigate('/admin/admins');
    } catch (error: any) {
      console.error('Erro ao atualizar administrador:', error);
      
      if (error.code === '23505') {
        toast.error('Este nome de usuário já está em uso');
      } else {
        toast.error('Erro ao atualizar administrador');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof AdminFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (isLoading && !formData.username) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div>Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/admin/admins')}
          className="h-9 w-9"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center space-x-2">
          <Shield className="h-6 w-6 text-navy-700" />
          <h2 className="text-2xl font-bold">Editar Administrador</h2>
        </div>
      </div>

      {/* Form Card */}
      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>Informações do Administrador</CardTitle>
        </CardHeader>
        <CardContent>
          {isSuperAdmin && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center space-x-2 text-amber-800">
                <Shield className="h-5 w-5" />
                <span className="font-semibold">Super Administrador Protegido</span>
              </div>
              <p className="text-sm text-amber-700 mt-2">
                Este é um Super Administrador e não pode ser editado por questões de segurança.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email (read-only) */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                disabled
                className="bg-gray-50"
              />
              <p className="text-xs text-muted-foreground">
                O email não pode ser alterado
              </p>
            </div>

            {/* Username */}
            <div className="space-y-2">
              <Label htmlFor="username">
                Nome de Usuário <span className="text-red-500">*</span>
              </Label>
              <Input
                id="username"
                value={formData.username}
                onChange={(e) => handleChange('username', e.target.value)}
                placeholder="Digite o nome de usuário"
                required
                disabled={isSuperAdmin || isLoading}
              />
            </div>

            {/* Full Name */}
            <div className="space-y-2">
              <Label htmlFor="full_name">
                Nome Completo <span className="text-red-500">*</span>
              </Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => handleChange('full_name', e.target.value)}
                placeholder="Digite o nome completo"
                required
                disabled={isSuperAdmin || isLoading}
              />
            </div>

            {/* Divider */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold mb-2">Alterar Senha (Opcional)</h3>
              {!isOwnProfile ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-amber-800">
                    <strong>Atenção:</strong> Você só pode alterar sua própria senha. Para alterar a senha deste administrador, peça para ele usar a opção "Alterar Senha" no menu lateral.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground mb-4">
                  Deixe em branco se não quiser alterar a senha
                </p>
              )}
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="new_password">Nova Senha</Label>
              <Input
                id="new_password"
                type="password"
                value={formData.new_password}
                onChange={(e) => handleChange('new_password', e.target.value)}
                placeholder="Digite a nova senha (mínimo 6 caracteres)"
                disabled={isSuperAdmin || isLoading || !isOwnProfile}
                autoComplete="new-password"
              />
              <p className="text-xs text-muted-foreground">
                Mínimo 6 caracteres com letras maiúsculas, minúsculas e números
              </p>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirmar Nova Senha</Label>
              <Input
                id="confirm_password"
                type="password"
                value={formData.confirm_password}
                onChange={(e) => handleChange('confirm_password', e.target.value)}
                placeholder="Digite a senha novamente"
                disabled={isSuperAdmin || isLoading || !isOwnProfile}
                autoComplete="new-password"
              />
            </div>

            {/* Status */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="space-y-0.5">
                <Label htmlFor="is_active">Status Ativo</Label>
                <p className="text-sm text-muted-foreground">
                  Administrador pode acessar o sistema
                </p>
              </div>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => handleChange('is_active', checked)}
                disabled={isSuperAdmin || isLoading}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/admins')}
                disabled={isLoading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSuperAdmin || isLoading}
              >
                <Save className="mr-2 h-4 w-4" />
                {isLoading ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminForm;
